<template lang="pug">
    .g-time-picker(elevation="0" min-height="290px", @mounted="setTime()")
        //- Blue title for displaying the date
        .g-time-picker__header.v-time-picker-title.v-picker__title.primaryHover.mb-2         
            .date-title()
                span.v-picker__title__btn.v-picker__title__btn--active {{formattedDate}}

        //- Arrow buttons for adding hours and minutes
        .g-time-picker__buttons.g-time-picker__buttons__add.px-4.py-4.d-flex
            v-btn.mx-12.g-arrow-button.g-arrow-button__hours(
                elevation="0",
                min-width="30",
                height="30",
                @click="addHour"     
            )
                v-icon(color="primaryHover", small) mdi-triangle

            v-btn.mx-12.g-arrow-button.g-arrow-button__minutes(
                elevation="0",
                min-width="30",
                height="30",
                @click="addMinute"
            )
                v-icon(color="primaryHover", small) mdi-triangle

        //- Time display inputs
        .g-time-picker-title.time-title.v-time-picker-title__time.py-2.d-flex
            
            v-text-field.v-picker__title__btn.px-4(
                v-model="hoursUI", 
                height="70px"
                placeholder="--",
                flat, 
                solo,
                light,
                maxlength="2",
                id="hoursInput",
                type="tel",
                min="1",
                max="12",
                background-color="background"
            ) 
            
            span.pb-8 :
            
            v-text-field.v-picker__title__btn.px-4(
                v-model="minutesUI",
                height="70px",
                placeholder="--"
                flat,
                solo,
                maxlength="2",
                id="minutesInput",
                type="tel",
                min="0",
                max="59",
                background-color="background"
            ) 

        //- Arrow buttons for substracting hours and minutes 
        .g-time-picker__buttons.g-time-picker__buttons__remove.px-4.d-flex
            v-btn.mx-12.g-arrow-button.g-arrow-button__hours(
                elevation="0",
                min-width="30",
                height="30",
                @click="removeHour"
            )
                v-icon.mdi-rotate-180(color="primaryHover", small) mdi-triangle

            v-btn.mx-12.g-arrow-button.g-arrow-button__minutes(
                elevation="0",
                min-width="30",
                height="30",
                @click="removeMinute"
            )
                v-icon.mdi-rotate-180(color="primaryHover", small) mdi-triangle 
        
        //- Offset selector
        .g-time-picker__select.px-4.pt-4.d-flex 
            v-select(
                label="Time Zone Offset",
                :value="offset",
                @change="offsetChange($event)",
                :items="offsetOptions",
                item-text="name",
                item-value="name"
            )
   
    
</template>

<script>
import moment from 'moment-timezone';
import isEmpty from 'lodash/isEmpty';

export default {
    name: 'GTimePicker',
    props: {
        /* Gets the selected date to be displayed on the timepicker header.
           For informative purposes only. */
        localDate: {
            type: String,
            default: null
        },
         /* The format of the date. How is gonna be displayed at the header of the time picker. */
        uiDateFormat: {
            type: String,
            default: "ddd, MMMM D"
        },
        value: {
            type: String,
            default: null
        },
        /* The offset provided from the datetimepicker, if applies. */
        offset: {
            type: String,
            default: "+00:00"
        },
    },
    data: function() {
        return {
            localHours: 0,
            localMinutes: 0,
            offsetOptions: [
                "-12:00", "-11:00", "-10:00", "-09:30", "-09:00", "-08:00", "-07:00", "-06:00", "-05:00", "-04:00", "-03:30", "-03:00", "-02:00", "-01:00",
                "+00:00", "+01:00", "+02:00", "+03:00", "+03:30", "+04:00", "+04:30", "+05:00", "+05:30", "+05:45", "+06:00", "+06:30", "+07:00", "+08:00",
                "+08:45", "+09:00", "+09:30", "+10:00", "+10:30",  "+11:00", "+12:00", "+12:45", "+13:00", "+14:00"
            ],
        }
    },
    computed: { 
        /*
            [COMPUTED] Returns the date formatted for the UI header display.
        */
        formattedDate() {
            return moment(this.localDate).format(this.uiDateFormat);
        },
        /*
            [COMPUTED] Returns the hours value for the display at UI.
            Sets the hours using 24-hours format.
        */
        hoursUI: {
            get: function() {
                if(this.localHours == null) return '';
                return this.localHours.toString();
            },
            set: function(newVal) {
                if(!isEmpty(newVal)) {
                    if(parseInt(newVal) > 99) newVal = 23;
                    this.validateHours(newVal);
                }
            }
        },
        /*
            [COMPUTED] Returns the minutes value for the display at UI.
            Sets the minutes with values from 0 to 59.
        */
        minutesUI: {
            get: function() {
                if(this.localMinutes == null) return '';
                return this.localMinutes.toString();
            },
            set: function(newVal) {
                if(!isEmpty(newVal)) {
                    if(parseInt(newVal) > 99) newVal = 59;
                    this.validateMinutes(newVal);
                }
            }  
        },
        /*
            [COMPUTED] Returns the full time values in the format HH:mm in 24-hours style.
        */
        currentTime() {
            if(this.localHours != null && this.localMinutes != null) {
                return this.twoDigitFormat(this.localHours) + ':' + this.twoDigitFormat(this.localMinutes);
            }
        },
    },
    watch: {
        /*
            [WATCH] If the currentTime computed changes, submit the value to the DateTimePicker component.
        */
        currentTime : {
            immediate: true,
            handler() {
                this.submit();
            }
        }
    },
    methods: {
        /*
            Adds one hour (when clicking the up arrow for hours) by updating the localhours variable.
            -----------------------------------------
            Parameter: none.
            returns: nothing.
        */
        addHour() {
            if(this.localHours == null || this.localHours < 0) this.localHours = 0;
            else this.localHours = this.localHours == 23 ? 0 : this.localHours + 1;
        },
        /*
            Removes one hour (when clicking the down arrow for hours) by updating the localhours variable.
            -----------------------------------------
            Parameter: none.
            returns: nothing.
        */
        removeHour(){
            if(this.localHours == null || this.localHours < 0) this.localHours = 23;
            else this.localHours = this.localHours == 0 ? 23 : this.localHours - 1;
        },
        /*
            Adds one minute (when clicking the up arrow for minutes) by updating the localminutes variable.
            -----------------------------------------
            Parameter: none.
            returns: nothing.
        */
        addMinute() {
            if(this.localMinutes == null || this.localMinutes < 1) this.localMinutes = 1;
            else this.localMinutes = this.localMinutes == 59 ? 0 : this.localMinutes + 1;
        },
        /*
            Removes one minute (when clicking the down arrow for minutes) by updating the localminutes variable.
            -----------------------------------------
            Parameter: none.
            returns: nothing.
        */
        removeMinute() {
            if(this.localMinutes == null || this.localMinutes <= 0) this.localMinutes = 59;
            else this.localMinutes = this.localMinutes == 0 ? 59 : this.localMinutes - 1;
        },
        /*
            Format the value in two-digit display, by adding a 0 where the value is less than 10.
            -----------------------------------------
            Parameter: the original value to be displayed..
            returns: the value in two-digit format. I.E. gets a "09" for 9, or a "12" for 12.
        */
        twoDigitFormat(tValue){
            return tValue < 10 ? '0' + tValue : tValue;
        },
        /*
            Checks for valid hours and fix the values to avoid issues with parent component.
            - If the hours value is not a number, make it null.
            - If the hours are less than 1, put the 1 as value.
            - If the hours are more than 23, put the 23 as value.
            -----------------------------------------
            Parameter: newHours value.
            returns: nothing. Changes are stored in localHours variable.
        */
        validateHours(newHours) {
            try{
                this.localHours = 0;
                let hours = parseInt(newHours);
                if(isNaN(hours)) {
                    this.localHours = null;
                } else {
                if(hours < 1) this.localHours = 1;
                else if(hours > 23) this.localHours = 23;
                else this.localHours = hours;
                }
            }catch(error) {
                console.log(this.$cleanString(error));
                this.localHours = null;
            }
        },
         /*
            Checks for valid minutes and fix the values to avoid issues with parent component.
            - If the minutes value is not a number, make it null.
            - If the minutes are less than 1, put the 1 as value.
            - If the minutes are more than 59, put the 59 as value.
            -----------------------------------------
            Parameter: newHours value.
            returns: nothing. Changes are stored in localHours variable.
        */
        validateMinutes(newMinutes) {
            try{
                let minutes = parseInt(newMinutes);
                if(isNaN(minutes)) {
                    this.localMinutes = null;
                }else {
                if(minutes < 0) this.localMinutes = 0;
                else if(minutes > 59) this.localMinutes = 59;
                else this.localMinutes = minutes;
                }
            }catch(error) {
                console.log(this.$cleanString(error));
                this.localMinutes = null;
            }
        },
        /*
            Checks for valid time format in a 24-hours format.
            -----------------------------------------
            Parameter: timeInput string containing the time.
            returns: boolean indicating if the input is properly formatted.
        */
        validateTimeFormat(timeInput) {
            var timeRegex = /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;
            return timeRegex.test(timeInput);
        },
         /*
            Emits the time value to the DateTimePicker component.
            -----------------------------------------
            Parameter: none.
            returns: nothing.
        */
        submit() {
            this.$emit('input', this.currentTime);
        },
         /*
            Emits the selected offset from the dropdown to the DateTimePicker component.
            -----------------------------------------
            Parameter: the new selected offset in string format.
            returns: nothing.
        */
        offsetChange(newOffset) {
            this.$emit('update:offset', newOffset);
        }
    },
    mounted() {
        try{
            if(!isEmpty(this.value) && this.validateTimeFormat(this.value)) {
                let timeComponents = this.value.split(':');
                this.localHours = parseInt(timeComponents[0]);
                this.localMinutes = parseInt(timeComponents[1]);
            }else {
                this.$emit('input', null);
            }
        }
        catch(error){
            console.log(this.$cleanString(error));
        }
    }
};
</script>

<style lang="scss" scoped>
@import "Timepicker.scss";
</style>

